import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, AlertOctagon, Plus } from "react-feather";
import {
  ButtonGroup,
  Button,
  Form,
  Modal,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { toast } from "react-toastify";
import {
  createSchedulingRuleAPI,
  disableRuleAPI,
  getSchedulingRulesAPI,
  updateRulePriorityAPI,
} from "../../../Services/mockApi";
import PageHeader from "../../../Components/shared/PageHeader";
import LoadingSkeleton from "../../../Components/shared/LoadingSkeleton";
import EmptyState from "../../../Components/shared/EmptyState";
import DataTableWrapper from "../../../Components/shared/DataTableWrapper";
import { companyUrl } from "../../../utils/mhNav";
import styles from "./RuleList.module.scss";

const CATS = [
  "All",
  "Working Hours",
  "Blackout",
  "Lead Time",
  "Buffer",
  "Resource",
];

export default function RuleList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [cat, setCat] = useState("All");
  const [createOpen, setCreateOpen] = useState(false);
  const [newRule, setNewRule] = useState({
    name: "",
    category: "Working Hours",
    scope: "global",
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSchedulingRulesAPI();
      setRows(res.data || []);
      setHasLoaded(true);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (cat === "All") return rows;
    return rows.filter((r) => r.category === cat);
  }, [rows, cat]);

  const movePriority = async (id, delta) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    try {
      await updateRulePriorityAPI(id, row.priority + delta);
      toast.success("Priority updated");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const columns = useMemo(
    () => [
      {
        name: "",
        width: "44px",
        cell: (r) =>
          r.conflicting ? (
            <OverlayTrigger
              overlay={
                <Tooltip>{r.conflictHint || "Potential conflict"}</Tooltip>
              }
            >
              <span className={styles.conflictIcon}>
                <AlertOctagon size={16} />
              </span>
            </OverlayTrigger>
          ) : null,
      },
      {
        name: "Rule name",
        selector: (r) => r.name,
        grow: 2,
        cell: (r) => (
          <span
            style={{
              fontSize: "0.84rem",
              fontWeight: 600,
              color: "var(--body-font-color)",
            }}
          >
            {r.name}
          </span>
        ),
      },
      {
        name: "Category",
        selector: (r) => r.category,
        width: "130px",
        cell: (r) => (
          <span
            style={{
              fontSize: "0.72rem",
              fontWeight: 700,
              letterSpacing: "0.04em",
              color: "var(--theme-default)",
              background: "rgba(100,73,152,0.08)",
              borderRadius: 999,
              padding: "2px 9px",
            }}
          >
            {r.category}
          </span>
        ),
      },
      {
        name: "Scope",
        selector: (r) => r.scope,
        width: "130px",
        cell: (r) => (
          <span
            style={{
              fontSize: "0.8rem",
              color: "var(--chart-text-color)",
              textTransform: "capitalize",
            }}
          >
            {r.scope}
          </span>
        ),
      },
      {
        name: "Priority",
        width: "150px",
        cell: (r) => (
          <div className="d-flex align-items-center gap-1">
            <button
              className={styles.priorityBtn}
              onClick={() => movePriority(r.id, 1)}
            >
              ↑
            </button>
            <span className={styles.priorityBadge}>{r.priority}</span>
            <button
              className={styles.priorityBtn}
              onClick={() => movePriority(r.id, -1)}
            >
              ↓
            </button>
          </div>
        ),
      },
      {
        name: "Effective from",
        selector: (r) =>
          new Date(r.effectiveFrom).toLocaleDateString([], {
            month: "short",
            day: "numeric",
            year: "numeric",
          }),
        width: "130px",
        cell: (r) => (
          <span
            style={{ fontSize: "0.8rem", color: "var(--chart-text-color)" }}
          >
            {new Date(r.effectiveFrom).toLocaleDateString([], {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        ),
      },
      {
        name: "Status",
        width: "90px",
        cell: (r) => (
          <span className={r.enabled ? styles.statusOn : styles.statusOff}>
            {r.enabled ? "On" : "Off"}
          </span>
        ),
      },
      {
        name: "Actions",
        width: "160px",
        cell: (r) => (
          <div className="d-flex gap-1 flex-wrap">
            <button
              className={styles.btnEdit}
              onClick={() => toast.info("Editor (mock)")}
            >
              Edit
            </button>
            <button
              className={styles.btnToggle}
              onClick={async () => {
                try {
                  await disableRuleAPI(r.id);
                  toast.success("Toggled");
                  load();
                } catch (e) {
                  toast.error(e.message);
                }
              }}
            >
              {r.enabled ? "Disable" : "Enable"}
            </button>
          </div>
        ),
      },
    ],
    [load, rows],
  );

  const breadcrumbs = [
    { label: "Company", path: companyUrl("dashboard") },
    { label: "Scheduling" },
  ];

  if (loading && !hasLoaded) {
    return (
      <div>
        <PageHeader title="Scheduling rules" breadcrumbs={breadcrumbs} />
        <LoadingSkeleton rows={6} cols={4} />
      </div>
    );
  }

  if (error && !hasLoaded) {
    return (
      <div>
        <PageHeader title="Scheduling rules" breadcrumbs={breadcrumbs} />
        <EmptyState
          icon={AlertTriangle}
          title="Could not load"
          description={error.message}
          actionLabel="Retry"
          onAction={load}
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Scheduling rules"
        breadcrumbs={breadcrumbs}
        primaryAction={{
          label: "Create rule",
          onClick: () => setCreateOpen(true),
        }}
        secondaryActions={[
          {
            label: "Working hours",
            onClick: () => navigate(companyUrl("scheduling/working-hours")),
            variant: "outline-secondary",
          },
          {
            label: "Resources",
            onClick: () => navigate(companyUrl("scheduling/resources")),
            variant: "outline-secondary",
          },
        ]}
      />

      {/* ── Category tab bar ── */}
      <ButtonGroup size="sm" className={styles.tabBar}>
        {CATS.map((c) => (
          <Button
            key={c}
            variant={cat === c ? "primary" : "outline-secondary"}
            onClick={() => setCat(c)}
          >
            {c}
            {/* live count chip per category */}
            {c !== "All" && (
              <span
                style={{
                  marginLeft: "0.35rem",
                  fontSize: "0.62rem",
                  fontWeight: 800,
                  background:
                    cat === c
                      ? "rgba(255,255,255,0.25)"
                      : "rgba(100,73,152,0.1)",
                  color: cat === c ? "#fff" : "var(--theme-default)",
                  borderRadius: 999,
                  padding: "1px 6px",
                }}
              >
                {rows.filter((r) => r.category === c).length}
              </span>
            )}
          </Button>
        ))}
      </ButtonGroup>

      {/* ── Table ── */}
      {!loading && filtered.length === 0 ? (
        <EmptyState
          icon={AlertTriangle}
          title="No rules"
          description="Try another category."
          actionLabel="Show all"
          onAction={() => setCat("All")}
        />
      ) : (
        <DataTableWrapper
          columns={columns}
          data={filtered}
          loading={loading}
          showSearch
          keyField="id"
        />
      )}

      {/* ── Create rule modal ── */}
      <Modal show={createOpen} onHide={() => setCreateOpen(false)} centered>
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title>
            <Plus
              size={14}
              className="me-1"
              style={{ color: "var(--theme-default)" }}
            />
            Create rule
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <Form.Group className="mb-3">
            <Form.Label>Type</Form.Label>
            <Form.Select
              value={newRule.category}
              onChange={(e) =>
                setNewRule((s) => ({ ...s, category: e.target.value }))
              }
            >
              {CATS.filter((c) => c !== "All").map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Name</Form.Label>
            <Form.Control
              placeholder="e.g. No meetings after 6 PM"
              value={newRule.name}
              onChange={(e) =>
                setNewRule((s) => ({ ...s, name: e.target.value }))
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <button
            className={styles.btnCreate}
            onClick={async () => {
              try {
                await createSchedulingRuleAPI(newRule);
                toast.success("Rule created");
                setCreateOpen(false);
                load();
              } catch (e) {
                toast.error(e.message);
              }
            }}
          >
            Create rule
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
